import { generateHTML } from '@tiptap/core';
import { ReactNode } from 'react';

import {
  INFO_BOX_DEFAULT_COLOUR,
  InfoBoxStyles,
  RICHTEXT_SETTINGS,
  ViewProps,
} from './info-box.constants';
import {
  getExtensions,
  isRichtextEmpty,
} from 'approot/shared/richtext/richtext.utils';
import {
  BORDER_STYLE_BOX,
  BORDER_STYLE_INSTRUCTION,
  BORDER_STYLE_NONE,
  BORDER_STYLE_NOTE,
  BORDER_STYLE_SPEECH,
  BORDER_STYLE_STATEMENT,
} from 'global/constants';
import {
  COLOUR_BLUE,
  COLOUR_PURPLE,
} from 'approot/shared/colour/colour.constants';
import {
  TTSComponentTrigger,
  ZoomComponentTrigger,
} from 'approot/activity-builder/activity-builder-zoom-component-modal';
import { ReactComponent as InfoBoxCornerSvg } from './info-box-corner.svg';
import * as StyledNote from './info-box-note.style';
import * as StyledTextBox from './info-text-box.style';
import * as StyledInfoBox from './info-box.style';
import * as StyledInfoBoxSpeech from './info-box-speech.style';
import * as StyledInfoBoxThought from './info-box-thought.style';
import * as StyledInfoStatementBox from './info-box-statement.style';
import * as StyledInfoInstructionBox from './info-box-instruction.style';
import { useTTSContext } from 'approot/activity-builder/tts/tts.context';
import { ttsGetTextBlockSelector } from 'approot/activity-builder/tts/logic/tts-get-text-block-selector';
import TTSButton from 'approot/activity-builder/tts/tts-button';
import TTSComponentMenu from 'approot/activity-builder/tts/tts-component-menu';
import { useActivityBuilderPresentContext } from 'approot/activity-builder/present/activity-builder-present.context';

const InfoBoxView = ({
  id,
  type,
  properties,
  openModal,
  isZoomed,
  isStudent,
}: ViewProps) => {
  const { parsedText } = useTTSContext({ id });
  const { activeComponent, setActiveComponent } =
    useActivityBuilderPresentContext();

  const isActive = !!isStudent && activeComponent?.id === id;
  const showTTSButton = isZoomed || isActive;

  const isAudioPlaying = showTTSButton && parsedText;

  const componentId = showTTSButton ? id : undefined;
  const style = properties.style;

  const extensions = getExtensions(RICHTEXT_SETTINGS);
  const richtext = isAudioPlaying
    ? parsedText
    : isRichtextEmpty(properties.richtext)
      ? ''
      : generateHTML(properties.richtext, extensions);

  if (!richtext) return null;

  const content = (
    <>
      {isStudent ? (
        <TTSComponentTrigger
          isActive={isActive}
          onClick={() => setActiveComponent(isActive ? undefined : { id })}
        />
      ) : (
        openModal && <ZoomComponentTrigger openModal={openModal} />
      )}

      {isZoomed && <TTSButton componentId={id} componentType={type} />}

      {style?.border === BORDER_STYLE_NOTE && (
        <InfoBoxViewNote
          id={componentId}
          richtext={richtext}
          isZoomed={isZoomed}
        />
      )}

      {style?.border === BORDER_STYLE_NONE && (
        <InfoBoxViewTextBox
          id={componentId}
          style={style}
          richtext={richtext}
          isZoomed={isZoomed}
        />
      )}

      {style?.border === BORDER_STYLE_STATEMENT && (
        <InfoBoxStatementBox
          id={componentId}
          style={style}
          richtext={richtext}
          isZoomed={isZoomed}
        />
      )}

      {style?.border === BORDER_STYLE_INSTRUCTION && (
        <InfoBoxInstructionBox
          id={componentId}
          style={style}
          richtext={richtext}
          isZoomed={isZoomed}
        />
      )}

      {style?.border === BORDER_STYLE_BOX && (
        <InfoBoxColouredBox
          id={componentId}
          style={style}
          richtext={richtext}
          isZoomed={isZoomed}
        />
      )}

      {style?.border === BORDER_STYLE_SPEECH && (
        <InfoBoxStyleBoxSpeech
          id={componentId}
          style={style}
          richtext={richtext}
          isZoomed={isZoomed}
        />
      )}
    </>
  );

  return isStudent ? (
    <TTSComponentMenu isActive={isActive} componentId={id} componentType={type}>
      {content}
    </TTSComponentMenu>
  ) : (
    content
  );
};

export default InfoBoxView;

type BaseProps = {
  id?: string;
  richtext: string;
  isZoomed?: boolean;
};

export const InfoBoxViewTextBox = ({
  id,
  richtext,
  isZoomed,
  style,
  isSmall,
}: BaseProps & {
  style?: InfoBoxStyles;
  isSmall?: boolean;
}) => {
  if (!richtext) return null;

  return (
    <StyledInfoBox.InfoBoxContainer $textAlignment={style?.textAlignment}>
      <StyledTextBox.InfoTextBox
        id={id ? ttsGetTextBlockSelector(id) : undefined}
        data-selector="modal-plain-box"
        $isZoomed={isZoomed}
        $width={style?.width}
        $isSmall={isSmall}
        dangerouslySetInnerHTML={{
          __html: richtext,
        }}
      />
    </StyledInfoBox.InfoBoxContainer>
  );
};

export const InfoBoxViewNote = ({ id, richtext, isZoomed }: BaseProps) => (
  <StyledNote.InfoBoxNote>
    <StyledNote.InfoBoxNoteCorner>
      <InfoBoxCornerSvg />
    </StyledNote.InfoBoxNoteCorner>
    <StyledNote.InfoBoxShadow />
    <StyledNote.InfoBoxNoteView
      id={id ? ttsGetTextBlockSelector(id) : undefined}
      $isZoomed={isZoomed}
      dangerouslySetInnerHTML={{
        __html: richtext,
      }}
    />
  </StyledNote.InfoBoxNote>
);

export const InfoBoxColouredBox = ({
  id,
  richtext,
  style,
  isZoomed,
}: BaseProps & {
  style: InfoBoxStyles;
}) => {
  if (!richtext) return null;

  return (
    <StyledInfoBox.InfoBoxContainer $textAlignment={style.textAlignment}>
      <StyledInfoBox.InfoBoxView
        id={id ? ttsGetTextBlockSelector(id) : undefined}
        data-selector="modal-coloured-box"
        $colour={style.baseColour || INFO_BOX_DEFAULT_COLOUR}
        $isZoomed={isZoomed}
        $width={style.width}
        dangerouslySetInnerHTML={{
          __html: richtext,
        }}
      />
    </StyledInfoBox.InfoBoxContainer>
  );
};

export const InfoBoxStatementBox = ({
  id,
  style,
  richtext,
  isZoomed,
}: BaseProps & {
  style: InfoBoxStyles;
}) => {
  if (!richtext) return null;

  return (
    <StyledInfoBox.InfoBoxContainer $textAlignment={style.textAlignment}>
      <StyledInfoStatementBox.InfoBoxStatementView
        id={id ? ttsGetTextBlockSelector(id) : undefined}
        data-selector="modal-statement-box"
        $colour={style.baseColour || COLOUR_PURPLE.base}
        $isZoomed={isZoomed}
        $width={style.width}
        dangerouslySetInnerHTML={{
          __html: richtext,
        }}
      />
    </StyledInfoBox.InfoBoxContainer>
  );
};

export const InfoBoxInstructionBox = ({
  id,
  style,
  richtext,
  isZoomed,
}: BaseProps & {
  style: InfoBoxStyles;
}) => {
  if (!richtext) return null;

  return (
    <StyledInfoBox.InfoBoxContainer $textAlignment={style.textAlignment}>
      <StyledInfoInstructionBox.InfoBoxInstructionView
        id={id ? ttsGetTextBlockSelector(id) : undefined}
        data-selector="modal-instruction-box"
        $colour={style.baseColour || COLOUR_BLUE.base}
        $isZoomed={isZoomed}
        $width={style.width}
        dangerouslySetInnerHTML={{
          __html: richtext,
        }}
      />
    </StyledInfoBox.InfoBoxContainer>
  );
};

export const InfoBoxStyleBoxSpeech = ({
  id,
  style,
  richtext,
  isZoomed,
}: BaseProps & {
  style: InfoBoxStyles;
}) => {
  if (!richtext) return null;

  return (
    <StyledInfoBox.InfoBoxContainer $textAlignment={style.textAlignment}>
      <StyledInfoBoxSpeech.InfoBoxSpeechView
        id={id ? ttsGetTextBlockSelector(id) : undefined}
        data-selector="modal-speech-bubble"
        $colour={style.baseColour || INFO_BOX_DEFAULT_COLOUR}
        $showBackgroundColour={!!style.backgroundColour}
        dangerouslySetInnerHTML={{
          __html: richtext,
        }}
        $borderStyle={style.border || BORDER_STYLE_BOX}
        $alignment={style.arrowAlignment || undefined}
        $position={style.arrowPosition || undefined}
        $isZoomed={isZoomed}
        $width={style.width}
      />
    </StyledInfoBox.InfoBoxContainer>
  );
};

export const InfoBoxStyleBoxSpeechInteractive = ({
  id,
  style,
  richtext,
  isZoomed,
  children,
}: BaseProps & {
  style: InfoBoxStyles;
  children?: ReactNode;
}) => {
  if (!richtext) return null;

  return (
    <StyledInfoBox.InfoBoxContainer $textAlignment={style.textAlignment}>
      <StyledInfoBoxSpeech.InfoBoxSpeechViewInteractive
        id={id ? ttsGetTextBlockSelector(id) : undefined}
        $colour={style.baseColour || INFO_BOX_DEFAULT_COLOUR}
        $showBackgroundColour={!!style.backgroundColour}
        $borderStyle={style.border || BORDER_STYLE_BOX}
        $alignment={style.arrowAlignment || undefined}
        $position={style.arrowPosition || undefined}
        $isZoomed={isZoomed}
        $width={style.width}
      >
        <div
          dangerouslySetInnerHTML={{
            __html: richtext,
          }}
        />
        {children}
      </StyledInfoBoxSpeech.InfoBoxSpeechViewInteractive>
    </StyledInfoBox.InfoBoxContainer>
  );
};

export const InfoBoxStyleThought = ({
  id,
  style,
  richtext,
  isZoomed,
  children,
}: BaseProps & {
  style: InfoBoxStyles;
  children?: ReactNode;
}) => {
  if (!richtext) return null;

  return (
    <StyledInfoBox.InfoBoxContainer $textAlignment={style.textAlignment}>
      <StyledInfoBoxThought.InfoBoxThoughtView
        $colour={style.baseColour || INFO_BOX_DEFAULT_COLOUR}
        $showBackgroundColour={!!style.backgroundColour}
        $borderStyle={style.border || BORDER_STYLE_BOX}
        $alignment={style.arrowAlignment || undefined}
        $position={style.arrowPosition || undefined}
        $isZoomed={isZoomed}
        $width={style.width}
      >
        <div
          id={ttsGetTextBlockSelector(id)}
          dangerouslySetInnerHTML={{
            __html: richtext,
          }}
        />
        {children}
      </StyledInfoBoxThought.InfoBoxThoughtView>
    </StyledInfoBox.InfoBoxContainer>
  );
};
